import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { useClickAway } from "react-use";
import { isPresent } from "@apl-digital/utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import classnames from "classnames";

import DropdownItem from "@/base/components/Global/PhoneField/DropdownItem";
import type { CountryCode } from "@/base/components/Global/PhoneField/types";
import { getCountrySearchResult } from "@/base/utils/countryCodeHelper";

import TextField from "../TextField";
const useStyles = createUseStyles(({ color, zIndex }) => ({
  container: {
    display: "flex",
    position: "relative",
  },
  countryContainer: {
    display: "flex",
    position: "relative",
    alignItems: "center",
    width: 50,
  },
  svg: {
    height: "100%",
    border: `1px solid ${color.fieldBorder}`,
  },
  arrow: {
    width: 0,
    height: 0,
    borderLeft: "5px solid transparent",
    borderRight: "5px solid transparent",
  },
  arrowUp: {
    borderBottom: `5px solid ${color.fieldIcon}`,
  },
  arrowDown: {
    borderTop: `5px solid ${color.fieldIcon}`,
  },
  dropdown: {
    display: "none",
    zIndex: zIndex.actionTopModal,
    position: "absolute",
    top: "calc(100% + 2px)",
    width: "calc(100% + 2px)",
    left: -1,
    height: 300,
    maxHeight: "25vh",
    backgroundColor: color.fieldBg,
    borderTop: "none",
  },
  open: {
    display: "block",
  },
  searchInput: {
    border: "none",
    "&:focus": {
      border: "none",
    },
  },
}));

type DropdownBlockProps = {
  countryCodes: CountryCode[];
  setCountry: (country: CountryCode) => void;
  setOpen: (isOpen: boolean) => void;
  isOpen: boolean;
};

const DropdownBlock: React.FC<DropdownBlockProps> = ({
  countryCodes,
  setCountry,
  setOpen,
  isOpen,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  useClickAway(dropdownRef, () => {
    setOpen(false);
  });

  const [search, setSearch] = useState("");
  const [currentCountryCodes, setCurrentCountryCodes] =
    useState<CountryCode[]>(countryCodes);

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
    }
    inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    const filteredCountryCodes = getCountrySearchResult(search, countryCodes);
    if (filteredCountryCodes.length === 1) {
      setCountry(filteredCountryCodes[0]);
      setOpen(false);
    } else {
      setCurrentCountryCodes(filteredCountryCodes);
    }
  }, [search]);

  const rowVirtualizer = useVirtualizer({
    count: currentCountryCodes.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,

    overscan: 5,
  });

  if (!isOpen) return null;

  return (
    <div
      className={classnames([classes.dropdown, isOpen && classes.open])}
      ref={dropdownRef}
    >
      <TextField
        setValue={setSearch}
        inputRef={inputRef}
        type="text"
        value={search}
        showClearButton
        placeholder={t("field_country_code_search")}
        override={classes.searchInput}
      />
      <div
        ref={parentRef}
        style={{
          height: `300px`,
          overflow: "auto",
        }}
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualItem) => {
            const item = currentCountryCodes[virtualItem.index];

            if (!isPresent(item)) {
              return null;
            }

            return (
              <div
                key={virtualItem.index}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <DropdownItem
                  key={virtualItem.key}
                  countryCode={item}
                  setCountry={(c: CountryCode) => {
                    setCountry(c);
                    setOpen(false);
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DropdownBlock;
